'use client'

import * as React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { LayoutDashboard, Award, FolderKanban, GanttChart, Network, TrendingUp, FileCheck, FileText, ShieldCheck, AlertTriangle, AlertCircle, Users, Handshake, DollarSign, CheckCircle, BarChart3, ShieldAlert, PackageCheck, UserCog, ScrollText, Settings, ChevronRight } from 'lucide-react'
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarFooter, SidebarRail } from 'components/ui/sidebar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible'
import { getNavigationForRole } from 'lib/navigation'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { useUser } from 'components/context/user-context'
import jooustLogo from 'images/jooust-logo.jpeg'

const iconMap= {
  LayoutDashboard,
  Award,
  FolderKanban,
  GanttChart,
  Network,
  TrendingUp,
  FileCheck,
  FileText,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  Users,
  Handshake,
  DollarSign,
  CheckCircle,
  BarChart3,
  ShieldAlert,
  PackageCheck,
  UserCog,
  ScrollText,
  Settings,
}

export function AppSidebar() {
  const { pathname } = useLocation()
  const { user, loading } = useUser()
  const currentUser = React.useMemo(() => ({
    name: user?.fullName || (loading ? 'Loading...' : 'User'),
    roleLabel: user?.userType || '',
    role: user?.userType || '',
  }), [user, loading])

  function getInitials(name) {
    if (!name) return 'U'
    return name
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  function formatRoleLabel(role) {
    if (!role) return ''
    return role.replace(/_/g, ' ')
  }
  const navigation = getNavigationForRole(currentUser.role)

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="p-4">
        <Link to="/dashboard" className="flex items-center gap-3">
          <img
            src={jooustLogo}
            alt="JOOUST Logo"
            className="size-9 shrink-0 rounded-lg object-cover"
          />
          <div className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="text-sm font-bold text-sidebar-foreground tracking-tight">
              JOOUST PMIS
            </span>
            <span className="text-[10px] text-sidebar-foreground/60 leading-tight">
              Project Management & MEL
            </span>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-2">
        {navigation.map((section) => (
          <SidebarGroup key={section.label}>
            <SidebarGroupLabel className="text-sidebar-foreground/50 text-[10px] uppercase tracking-wider font-semibold">
              {section.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const Icon = iconMap[item.icon] || LayoutDashboard
                  const isActive =
                    pathname === item.href ||
                    (item.href !== '/dashboard' && pathname.startsWith(item.href))

                  if (item.children) {
                    return (
                      <Collapsible key={item.title} defaultOpen={isActive}>
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton tooltip={item.title}>
                              <Icon className="size-4" />
                              <span>{item.title}</span>
                              <ChevronRight className="ml-auto size-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenu className="ml-4 mt-1 border-l border-sidebar-border pl-2">
                              {item.children.map((child) => (
                                <SidebarMenuItem key={child.href}>
                                  <SidebarMenuButton
                                    asChild
                                    isActive={pathname === child.href}
                                    size="sm"
                                  >
                                    <Link to={child.href}>
                                      <span>{child.title}</span>
                                    </Link>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>
                              ))}
                            </SidebarMenu>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    )
                  }

                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.title}
                      >
                        <Link to={item.href}>
                          <Icon className="size-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="p-3">
        <div className="flex items-center gap-3 rounded-lg p-2 group-data-[collapsible=icon]:justify-center">
          <Avatar className="size-8 shrink-0">
            <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-semibold">
              {getInitials(currentUser.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="text-xs font-medium text-sidebar-foreground truncate max-w-[140px]">
              {currentUser.name}
            </span>
            {currentUser.roleLabel ? (
              <span className="text-[10px] text-sidebar-foreground/60 capitalize">
                {currentUser.roleLabel}
              </span>
            ) : null}
          </div>
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
